import { useState } from "react";
import { useGameStore } from "../../app/gameStore";
import { useRunStore } from "../../app/runStore";
import { ReagentChip } from "../ReagentChip";
import "./stage-picker.css";

/**
 * The job sheet: which synthesis is on the bench, and how the shift is going.
 *
 * In a run it is a progress board, not a menu — stages come in order and a
 * failed one ends the shift, which is what makes the order mean anything.
 * Picking a stage by hand is free play, a balance-pass mode reached from the
 * tuning console; when that is on, the list becomes selectable.
 */
export function StagePicker() {
  const stages = useGameStore((s) => s.stages);
  const pickable = useGameStore((s) => s.pickable);
  const stageId = useGameStore((s) => s.stageId);
  const mode = useGameStore((s) => s.mode);
  const selectStage = useGameStore((s) => s.selectStage);
  const run = useRunStore((s) => s.run);
  const [open, setOpen] = useState(false);

  const current = pickable.find((s) => s.id === stageId);
  const isRun = mode === "run";

  return (
    <div className={`stage-picker${open ? " is-open" : ""}`}>
      <button className="stage-picker__current" onClick={() => setOpen((v) => !v)}>
        <span className="stage-picker__eyebrow">
          {isRun ? `batch ${Math.min(run.stageIndex + 1, stages.length)} of ${stages.length}` : "free play"}
        </span>
        <span className="stage-picker__name">{current?.name ?? "—"}</span>
        <span className="stage-picker__blurb">{current?.blurb}</span>

        {isRun && (
          <span className="stage-picker__pips" aria-hidden>
            {stages.map((stage, i) => {
              const result = run.results[i];
              const state = result
                ? result.cleared
                  ? "is-done"
                  : "is-failed"
                : i === run.stageIndex
                  ? "is-current"
                  : "";
              return <span key={stage.id} className={`stage-picker__pip ${state}`} />;
            })}
          </span>
        )}

        <span className="stage-picker__chevron" aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open && (
        <ul className="stage-picker__list">
          {pickable.map((stage) => {
            // Position in the SHIFT, not in this list — the induction is not
            // a batch, so it has no result and is never locked.
            const batch = stages.findIndex((s) => s.id === stage.id);
            const result = batch === -1 ? undefined : run.results[batch];
            const reached = !isRun || batch === -1 || batch <= run.stageIndex;
            return (
              <li key={stage.id}>
                <button
                  className={[
                    stage.id === stageId ? "is-current" : "",
                    reached ? "" : "is-locked",
                  ].join(" ")}
                  disabled={isRun && !reached}
                  onClick={() => {
                    selectStage(stage.id);
                    setOpen(false);
                  }}
                >
                  <span className="stage-picker__index">
                    {batch === -1 ? "00" : String(batch + 1).padStart(2, "0")}
                  </span>
                  <span className="stage-picker__body">
                    <span className="stage-picker__name">{stage.name}</span>
                    <span className="stage-picker__blurb">{stage.blurb}</span>
                  </span>
                  <span className="stage-picker__quota">
                    {isRun && result ? (
                      result.cleared ? (
                        `${result.creditsEarned}₵`
                      ) : (
                        "scrapped"
                      )
                    ) : (
                      <ReagentChip composition={stage.objective.targetComposition} />
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
